import { ApiError, ErrorCode, type CompanyResponse } from '../../api';
import { SCOPE_MEANINGS } from './staffUsers';

/**
 * The words the economic-group screens use, and the one reading of a refusal
 * they share.
 *
 * A lookup module like [`onboarding`](./onboarding.ts), and here for the same
 * reason: a group is named on its own three screens, on the company detail and
 * as a column of the companies list, and "grupo econômico" spelled two ways
 * across them is how a screen stops being trusted.
 *
 * Two facts the copy has to carry, because the screens cannot show them any
 * other way:
 *
 *  - **A company is in one group at most.** Putting it in a second one is a
 *    move, not an addition, and the sentence next to every place that does it
 *    says so before the click rather than after.
 *  - **A group changes nobody's access.** It links companies for the people who
 *    work them; the HR of each company still sees only its own.
 *
 * There is no delete, and so no word for one.
 */
export const GROUPS_COPY = {
  title: 'Grupos econômicos',
  description:
    'Empresas clientes do mesmo grupo econômico, reunidas para quem trabalha com elas. Um grupo ' +
    'não muda o acesso de ninguém: o RH de cada empresa continua vendo só a própria.',

  // --- The list ------------------------------------------------------------
  listTitle: 'Grupos cadastrados',
  listDescription: 'Abra um para ver as empresas dele, renomeá-lo e mudar quem faz parte.',
  loading: 'Carregando grupos…',
  error: 'Não foi possível carregar os grupos',
  none: 'Nenhum grupo cadastrado',
  noneDescription:
    'Cadastre o primeiro em "Cadastrar grupo". As empresas entram depois, pela página do grupo ' +
    'ou pela de cada empresa.',
  caption: 'Grupos econômicos cadastrados',
  memberCountLabel: 'Empresas',
  createdAtLabel: 'Cadastrado em',
  // Filtering and paging the list (#440), in the companies list's words so the
  // two screens read the same. A column filter that leaves nothing keeps the
  // table, so its inputs stay where they can be changed, and says so inside
  // it; its way out is worded apart from the toolbar's own "Limpar filtros".
  noMatchingFilteredGroups: 'Nenhum grupo corresponde aos filtros',
  noMatchingFilteredGroupsDescription:
    'Nenhum grupo atende a tudo o que foi pedido nas colunas. Altere um filtro ou limpe todos.',
  clearGroupsFilters: 'Mostrar todos os grupos',
  // A page past the end: an old link after the list shrank, or a typed number.
  groupsPageOutOfRange: 'Esta página não existe mais',
  groupsPageOutOfRangeDescription: 'A lista tem menos páginas do que o endereço pede.',
  groupsFirstPage: 'Ir para a primeira página',

  // --- Registering one -----------------------------------------------------
  register: 'Cadastrar grupo',
  registering: 'Cadastrando…',
  registered: 'Grupo cadastrado.',
  registerDescription:
    'Um nome e um identificador. O grupo nasce vazio; as empresas entram depois, pela página ' +
    'dele.',
  nameLabel: 'Nome',
  nameHint: 'Como o grupo aparece nas telas.',
  slugLabel: 'Identificador',
  slugHint:
    'Minúsculas, sem acento e sem espaço — "grupo-exemplo". Único entre os grupos e definitivo: ' +
    'renomear o grupo depois não muda o identificador.',
  back: 'Voltar para a lista de grupos',
  cancel: 'Cancelar',

  // --- One group -----------------------------------------------------------
  groupLoading: 'Carregando grupo…',
  groupError: 'Não foi possível carregar o grupo',
  notFound: 'Grupo não encontrado',
  notFoundDescription:
    'Este endereço aponta para um grupo que não existe, ou que esta conta não pode ver. Volte ' +
    'para a lista e abra um de lá.',
  renameTitle: 'Nome do grupo',
  renameDescription:
    'Mudar o nome muda como o grupo aparece em todas as telas. O identificador fica como está.',
  slugReadOnly: 'O identificador não muda depois do cadastro.',
  saveName: 'Salvar nome',
  savingName: 'Salvando…',
  renamed: 'Nome do grupo atualizado.',
  nothingChanged: 'O nome é o mesmo de antes. Altere-o antes de salvar.',
  membersTitle: 'Empresas do grupo',
  membersDescription: 'Abra uma para ver os contratos e as pessoas dela.',
  membersCaption: 'Empresas deste grupo',
  noMembers: 'Nenhuma empresa neste grupo',
  noMembersDescription: 'Adicione a primeira pelo botão "Adicionar empresa", acima desta lista.',
  companyNameLabel: 'Razão social',
  companySlugLabel: 'Identificador',
  companyCnpjLabel: 'CNPJ',
  actionsLabel: 'Ações',
  remove: 'Remover do grupo',
  removeRefused: 'Não foi possível remover a empresa do grupo',
  // The "add a company" dialog (#428): a search over the registry, paged by
  // the server, newest registration first.
  addTitle: 'Adicionar empresa',
  addDescription:
    'Escolha uma empresa que ainda não está neste grupo. Uma empresa fica em um grupo só: se a ' +
    'escolhida já estiver em outro, ela sai de lá e vem para este.',
  addIntro: 'Empresas sem grupo, das cadastradas mais recentemente às mais antigas.',
  searchLabel: 'Buscar por nome, identificador ou CNPJ',
  searchHint: 'Pode digitar só uma parte. O CNPJ vale com ou sem pontuação.',
  includeGrouped: 'Incluir empresas que já estão em outro grupo',
  candidatesCaption: 'Empresas que podem entrar neste grupo',
  companyLabel: 'Empresa',
  add: 'Adicionar ao grupo',
  adding: 'Adicionando…',
  addRefused: 'Não foi possível adicionar a empresa ao grupo',
  companiesLoading: 'Carregando empresas…',
  companiesError: 'Não foi possível carregar as empresas',
  noCandidates: 'Todas as empresas cadastradas já estão neste grupo.',
  noCandidatesUngrouped: 'Nenhuma empresa sem grupo',
  noCandidatesUngroupedDescription:
    'Todas as empresas cadastradas já estão em algum grupo. Marque "Incluir empresas que já ' +
    'estão em outro grupo" para trazer uma de lá.',
  noMatches: 'Nenhuma empresa encontrada para esta busca',
  noMatchesDescription:
    'Confira a grafia do nome, o identificador ou os números do CNPJ. Se a empresa já estiver ' +
    'em outro grupo, marque "Incluir empresas que já estão em outro grupo".',
  noMatchesWidenedDescription: 'Confira a grafia do nome, o identificador ou os números do CNPJ.',

  // --- Who the group insures (#419) -----------------------------------------
  //
  // The company page's Segurados panel at group level, in the same words:
  // the column headers are `SEGURADOS_COPY`'s, shared through the one table.
  seguradosTitle: 'Segurados',
  seguradosDescription:
    'Quem está coberto hoje por qualquer contrato de qualquer empresa deste grupo, uma linha por ' +
    'pessoa. Movimentações agendadas aparecem na página de cada empresa.',
  seguradosLoading: 'Carregando segurados…',
  seguradosError: 'Não foi possível carregar os segurados do grupo',
  seguradosNoMembers: 'Nenhuma empresa, nenhum segurado',
  seguradosNoMembersDescription:
    'Os segurados do grupo são os das empresas dele. Adicione a primeira pelo botão "Adicionar ' +
    'empresa", em "Empresas do grupo".',
  seguradosEmpty: 'Ninguém coberto hoje',
  seguradosEmptyDescription:
    'As empresas do grupo ainda não têm contratos, ou os contratos ainda não têm população. ' +
    'Cadastre os contratos na página de cada empresa e importe a planilha do grupo em ' +
    '"Importar população inicial do grupo", logo abaixo.',
  seguradosNoMatchesHint:
    'Confira a grafia do nome, os números do CPF, o contrato e a empresa escolhidos, ou use ' +
    '"Limpar filtros".',
  seguradosCaption: 'Pessoas cobertas pelos contratos das empresas do grupo nesta data',

  // --- The people registered at the group (#391, #421) ---------------------
  //
  // The company page's users panel at group level. The fields and the row are
  // read with the same words as there; only what the panel is *about* changes,
  // and the sentence on reach is `staffUsers`' own, so the staff-wide form and
  // this panel say the same thing about a group user.
  usersTitle: 'Usuários do grupo',
  usersDescription:
    'Quem entra no portal por este grupo, com qual permissão, e como um acesso é encerrado. ' +
    `${SCOPE_MEANINGS.group} ` +
    'Desativar não é apagar: a conta para de entrar e continua na lista, marcada.',
  usersLoading: 'Carregando pessoas…',
  usersError: 'Não foi possível carregar as pessoas do grupo',
  noUsers: 'O grupo ainda não tem usuários',
  noUsersDescription:
    'Cadastre o primeiro em "Cadastrar pessoa". Quem é cadastrado na empresa aparece na página ' +
    'da empresa, não aqui.',
  usersCaption: 'Pessoas cadastradas neste grupo que entram no produto',
  userScopeNote:
    `${SCOPE_MEANINGS.group} ` + 'Equipe vyttech se cadastra pela lista de usuários, não por aqui.',

  // --- A company's group, on the company's own page ------------------------
  companyGroupTitle: 'Grupo econômico',
  companyGroupDescription:
    'O grupo econômico desta empresa. Uma empresa fica em um grupo só, então escolher outro a ' +
    'tira do atual. Não muda o acesso de ninguém: o RH desta empresa continua vendo só ela.',
  noGroup: 'Sem grupo',
  groupLabel: 'Grupo',
  groupPlaceholder: 'Escolha um grupo…',
  saveGroup: 'Salvar grupo',
  savingGroup: 'Salvando…',
  clearGroup: 'Tirar do grupo',
  groupSaved: 'Grupo da empresa atualizado.',
  groupCleared: 'A empresa saiu do grupo.',
  noGroups: 'Nenhum grupo cadastrado ainda',
  noGroupsDescription: 'Cadastre um em "Grupos econômicos" para poder colocar a empresa nele.',
  groupUnchanged: 'A empresa já está neste grupo.',

  // --- A slug already taken --------------------------------------------------
  //
  // The API answers a duplicate slug with a bare `409` whose details name no
  // field, and the shared wording for a conflict ("alterada por outra pessoa")
  // would be wrong here: nothing was edited concurrently, the slug is taken.
  slugTakenForm:
    'Já existe um grupo com este identificador. Escolha outro, ou procure o grupo na lista ' +
    'antes de cadastrá-lo de novo.',
} as const;

/** The field-level half of a taken slug, naming the slug that was refused. */
export function slugTakenField(slug: string): string {
  return `Já existe um grupo com o identificador "${slug}".`;
}

/** A `409` from `POST /groups` means one thing: the slug is taken. */
export function isSlugTaken(error: unknown): boolean {
  return error instanceof ApiError && error.code === ErrorCode.CONFLICT;
}

/**
 * How a company reads in the "add a company" dialog of one group.
 *
 * A company already in another group says which, because choosing it moves it
 * out of there -- the person deciding should see what they are taking it from.
 */
export function candidateLabel(company: CompanyResponse): string {
  const group = company.group ?? null;
  return group === null ? company.name : `${company.name} (hoje em ${group.name})`;
}
